<template>
	<base-layout>
		<template v-slot:main>
			<div class="mt-4">
				<nav>
					<router-link v-if='!isEditMode' to="/attendees"
						class="flex items-center text-sm leading-5 font-medium text-gray-400 hover:text-gray-200 focus:outline-none focus:underline transition duration-150 ease-in-out">
						<svg class="flex-shrink-0 -ml-1 mr-1 h-5 w-5 text-gray-500" fill="currentColor"
							viewBox="0 0 20 20">
							<path fill-rule="evenodd"
								d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
								clip-rule="evenodd" />
						</svg>
						Back to attendees
					</router-link>
				</nav>
				<div class="block sm:flex sm:justify-between sm:items-start">
					<div class="sm:float-left">
						<div class="mt-1 mb-2 md:flex md:items-center md:justify-between">
							<!-- <div v-if="isFetchingCompanyInfo" class="w-full h-40 bg-gray-300 rounded animate-pulse"></div> -->
							<h1 v-if="selectedCompany"
								class="flex-1 min-w-0 font-bold mt-2 leading-7 text-gray-900 text-2xl sm:text-3xl leading-9 sm:truncate tracking-tight">
								{{ selectedCompany.name }}
							</h1>
						</div>
						<div v-if="selectedCompany" class="mt-1 hidden sm:flex flex-col sm:mt-0 sm:flex-row sm:flex-wrap">
							<div v-if='selectedCompany.website && selectedCompany.website !== "-"'
								class="mt-2 flex items-center text-sm leading-5 text-gray-500 sm:mr-6">
								<svg class="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400" viewBox="0 0 20 20"
									fill="currentColor">
									<path
										d="M11 3a1 1 0 100 2h2.586l-6.293 6.293a1 1 0 101.414 1.414L15 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1h-5z" />
									<path
										d="M5 5a2 2 0 00-2 2v8a2 2 0 002 2h8a2 2 0 002-2v-3a1 1 0 10-2 0v3H5V7h3a1 1 0 000-2H5z" />
								</svg>
								<a class='hover:underline' :href="normalizeWebsiteUrl(selectedCompany.website)"
									target="_blank" rel="noopener">{{ selectedCompany.website }}</a>
							</div>
							<div v-if="selectedCompany.country"
								class="mt-2 flex items-center text-sm leading-5 text-gray-500 sm:mr-6">
								<svg class="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400" fill="currentColor"
									viewBox="0 0 20 20">
									<path fill-rule="evenodd"
										d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
										clip-rule="evenodd" />
								</svg>
								<span>{{ selectedCompany.country }}</span>
							</div>
							<div v-if="selectedCompany.tel"
								class="mt-2 flex items-center text-sm leading-5 text-gray-500 sm:mr-6">
								<svg class="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400" viewBox="0 0 20 20"
									fill="currentColor">
									<path
										d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
								</svg>
								<a class='hover:underline' :href="selectedCompanyEnriched.telLink">{{
									selectedCompany.tel }}</a>
							</div>
						</div>
					</div>

					<div class="flex flex-col sm:flex-row sm:items-center mt-6 gap-3">

						<!-- Left button -->
						<div v-if="showDownloadSharingBanner" @click="downloadSharingBanner">
							<div
								class="bg-red-600 text-white rounded-full text-center cursor-pointer py-2 px-4 flex items-center justify-center gap-2 mx-2">
								<svg fill="#ffff" width="25px" height="25px" viewBox="0 0 24 24"
									xmlns="http://www.w3.org/2000/svg">
									<path
										d="M20,7a2,2,0,0,0-1.18.39l-1.75-.8L19,5.71A2,2,0,0,0,20,6a2,2,0,1,0-2-2l-1.89.87A2,2,0,1,0,15,8.5a1.88,1.88,0,0,0,.92-.24l2.1,1A2,2,0,1,0,20,7Zm-1,6a1,1,0,0,0-1,1v.39l-1.48-1.48a2.77,2.77,0,0,0-3.93,0l-.7.7L9.41,11.12a2.79,2.79,0,0,0-3.93,0L4,12.6V7A1,1,0,0,1,5,6h5a1,1,0,0,0,0-2H5A3,3,0,0,0,2,7V19a3,3,0,0,0,3,3H17a3,3,0,0,0,3-3V14A1,1,0,0,0,19,13ZM5,20a1,1,0,0,1-1-1V15.43l2.9-2.9a.79.79,0,0,1,1.09,0l3.17,3.17,0,0L15.45,20Zm13-1a.89.89,0,0,1-.18.53L13.31,15l.7-.7a.78.78,0,0,1,1.1,0L18,17.22Z" />
								</svg>
								<span class="font-semibold">Download Sharing Banner</span>
							</div>
						</div>

						<!-- Right logo -->
						<div v-if="isLogoLoaded"
							class="ml-auto bg-white border border-gray-300 shadow-md p-2 rounded cursor-pointer hover:border-gray-400 transition-border duration-300">
							<a :href="normalizeWebsiteUrl(selectedCompany.website)" target="_blank" rel="noopener">
								<img :src="companyLogoURL" class="max-h-16 object-cover">
							</a>
						</div>

					</div>

				</div>

				<div class="mt-6">
					<div class="grid grid-cols-12 gap-6">
						<div class="col-span-12 lg:col-span-6 bg-white shadow overflow-hidden sm:rounded-lg pb-4">
							<div class="px-4 py-5 border-b border-gray-200 sm:px-6">
								<!-- <div class='float-right' v-if='selectedCompanyEnriched.picUrl' :style='selectedCompanyEnriched.picCSS'></div> -->
								<h3 class="text-lg leading-6 font-medium text-gray-900">Company Details</h3>

								<div v-if="selectedCompanyEnriched" class='float-right -mt-8'>
									<div v-if='selectedCompanyEnriched.isTrusted' class='member_trusted'
										title="This company is a 'Trusted Member'. Trusted members have been on www.EuropeanBrokerMeeting.com for 3 or more years, we do not have any unsolved complaints on file and they offer a 1 month return to base warranty.">
									</div>
									<div v-else-if='selectedCompanyEnriched.isGold' class='member_gold'
										title="This company is a 'Gold Member'. Gold members have been on www.EuropeanBrokerMeeting.com for 10 or more years, we do not have any unsolved complaints on file and they offer a 1 month return to base warranty.">
									</div>
									<div v-else-if='selectedCompanyEnriched.isDiamond' class='member_diamond'
										title="This company is a 'Diamond Member'. Diamond members have been on www.EuropeanBrokerMeeting.com for 20 or more years, we do not have any unsolved complaints on file and they offer a 1 month return to base warranty.">
									</div>
								</div>
							</div>

							<div v-if="isFetchingCompanyInfo" class="px-5">
								<div class="grid grid-cols-[2fr_3fr] gap-4 py-5 px-1">
									<div class="space-y-3 animate-pulse">
										<div class="h-6 bg-gray-300 rounded w-full"></div>
										<div class="h-6 bg-gray-300 rounded w-full"></div>
										<div class="h-6 bg-gray-300 rounded w-full"></div>
										<div class="h-6 bg-gray-300 rounded w-full"></div>
										<div class="h-6 bg-gray-300 rounded w-full"></div>
										<div class="h-6 bg-gray-300 rounded w-full"></div>
										<div class="h-6 bg-gray-300 rounded w-full"></div>
									</div>
									<div class="space-y-3 animate-pulse">
										<div class="h-6 bg-gray-300 rounded w-full"></div>
										<div class="h-6 bg-gray-300 rounded w-full"></div>
										<div class="h-6 bg-gray-300 rounded w-4/5"></div>
										<div class="h-6 bg-gray-300 rounded w-full"></div>
										<div class="h-6 bg-gray-300 rounded w-full"></div>
										<div class="h-6 bg-gray-300 rounded w-4/5"></div>
										<div class="h-40 bg-gray-300 rounded w-full"></div>
									</div>
								</div>
							</div>

							<div v-else>
								<dl class='mt-2 mb-8 max-h-120 overflow-y-auto'>
									<div class=" px-4 py-2 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
										<dt class="text-sm leading-5 font-medium text-gray-500">Name</dt>
										<dd class="mt-1 text-sm leading-5 text-gray-900 sm:mt-0 sm:col-span-2">{{
											selectedCompanyEnriched.name
										}}
										</dd>
									</div>

									<div v-if='user'>
										<div class=" px-4 py-2 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
											<dt class="text-sm leading-5 font-medium text-gray-500">Telephone</dt>
											<dd class="mt-1 text-sm leading-5 text-gray-900 sm:mt-0 sm:col-span-2">

												<a v-if='!isEditMode' class='text-red-700'
													:href="selectedCompanyEnriched.telLink">{{
														selectedCompanyEnriched.tel }}</a>
												<input v-else class='form-input block w-full'
													v-model="selectedCompanyEnriched.tel">
											</dd>
										</div>
										<div class=" px-4 py-2 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
											<dt class="text-sm leading-5 font-medium text-gray-500">Website</dt>
											<dd class="mt-1 text-sm leading-5 text-gray-900 sm:mt-0 sm:col-span-2">
												<div v-if='!isEditMode'>
													<a v-if='selectedCompanyEnriched.website && selectedCompanyEnriched.website !== "-"'
														class='text-red-700'
														:href="normalizeWebsiteUrl(selectedCompanyEnriched.website)"
														target="_blank">{{
															selectedCompanyEnriched.website }}</a>
													<div v-else>-</div>
												</div>
												<input v-else class='form-input block w-full'
													v-model="selectedCompanyEnriched.website"
													@keydown.space="(event) => event.preventDefault()">
											</dd>
										</div>
										<div class="bg-white px-4 py-2 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
											<dt class="text-sm leading-5 font-medium text-gray-500">{{ vatLabel }}</dt>
											<dd class="mt-1 text-sm leading-5 text-gray-900 sm:mt-0 sm:col-span-2">{{
												selectedCompanyEnriched.vat }}
											</dd>
										</div>
										<div class=" px-4 py-2 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
											<dt class="text-sm leading-5 font-medium text-gray-500">Address</dt>
											<dd class="mt-1 text-sm leading-5 text-gray-900 sm:mt-0 sm:col-span-2">
												<a v-if='!isEditMode && selectedCompanyEnriched.mapUrl'
													:href='selectedCompanyEnriched.mapUrl' target='_blank'
													class='text-red-700'>{{ selectedCompanyEnriched.address }}</a>
												<div v-else-if='!isEditMode && !selectedCompanyEnriched.mapUrl'
													target='_blank'>
													{{ selectedCompanyEnriched.address }}</div>
												<input v-else class='form-input block w-full'
													v-model="selectedCompanyEnriched.address">
											</dd>
										</div>
										<div class="bg-white px-4 py-2 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
											<dt class="text-sm leading-5 font-medium text-gray-500">Country</dt>
											<dd class="mt-1 text-sm leading-5 text-gray-900 sm:mt-0 sm:col-span-2">{{
												selectedCompanyEnriched.country
											}}
											</dd>
										</div>
										<div v-if="!isEditMode">
											<div v-if="selectedCompanyEnriched.industry">
												<div class="bg-white px-4 py-2 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
													<dt class="text-sm leading-5 font-medium text-gray-500">Industry
													</dt>
													<dd
														class="mt-1 text-sm leading-5 text-gray-900 sm:mt-0 sm:col-span-2">
														{{
															selectedCompanyEnriched.industry
														}}
													</dd>
												</div>
											</div>
											<div v-else>
												<div class="bg-white px-4 py-2 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
													<dt class="text-sm leading-5 font-medium text-gray-500">Main
														Interest</dt>
													<dd
														class="mt-1 text-sm leading-5 text-gray-900 sm:mt-0 sm:col-span-2">
														{{
															selectedCompanyEnriched.categories
														}}
													</dd>
												</div>
												<div class="bg-white px-4 py-2 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
													<dt class="text-sm leading-5 font-medium text-gray-500">Trades
														Mostly</dt>
													<dd
														class="mt-1 text-sm leading-5 text-gray-900 sm:mt-0 sm:col-span-2">
														{{
															selectedCompanyEnriched.tradingType
														}}
													</dd>
												</div>
											</div>
										</div>
									</div>

									<div class="px-4 py-2 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
										<dt class="text-sm leading-5 font-medium text-gray-500">
											Company Description<br>
											<span v-if="isEditMode" class="text-xs text-red-600"><i>{{
												remainingCharacters }} characters remaining</i></span>
										</dt>
										<dd class="mt-1 text-sm leading-5 text-gray-900 sm:mt-0 sm:col-span-2">
											<div v-if='!isEditMode' class="whitespace-pre-line">{{
												selectedCompanyEnriched.profile }}</div>
											<textarea v-else class='form-input block w-full'
												v-model="selectedCompanyEnriched.profile" rows="6"></textarea>
										</dd>
									</div>
								</dl>

								<div v-if='isEditMode' class="px-4 sm:px-6">
									<!-- Main Interests -->
									<div class="sm:grid sm:grid-cols-2 sm:gap-8">
										<div>
											<div class="text-base font-semibold leading-6 text-gray-900">Main interests
											</div>
											<p class="mt-1 max-w-2xl text-sm text-gray-500">Choose a minimum of 1 and a
												maximum of 3</p>
											<p v-if="errors.length" class="mt-2 text-sm text-red-600">{{ errors[0] }}
											</p>
										</div>
										<div>
											<div class="mt-2 sm:mt-0 max-w-lg space-y-4">
												<div v-for="catOption in categoryOptionsEnriched"
													:key="catOption.vueKey" class="relative flex items-start">
													<div class="flex h-5 items-center">
														<input :id="catOption.vueKey" :value="catOption.id"
															type="checkbox"
															class="h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
															v-model="selectedCategories"
															:disabled="isCheckboxDisabled(catOption.id)" />
													</div>
													<div class="ml-3 text-sm">
														<label :for="catOption.vueKey"
															class="font-medium text-gray-700">{{ catOption.value
															}}</label>
													</div>
												</div>
											</div>
										</div>
									</div>

									<!-- Trade Preferences -->
									<div class="sm:grid sm:grid-cols-2 sm:gap-8 mt-10">
										<div>
											<div class="text-base font-semibold text-gray-900 sm:text-gray-700">We trade
												mostly in</div>
											<p class="mt-1 max-w-2xl text-sm text-gray-500">Please choose your trade
												preferences</p>
										</div>
										<div>
											<div class="mt-2 sm:mt-0 max-w-lg space-y-4">
												<div class="flex items-center">
													<input id="used-equipment" name="trade-type" type="radio"
														value="used"
														class="h-4 w-4 border-gray-300 text-red-600 focus:ring-red-500"
														v-model="selectedTradeType" />
													<label for="used-equipment"
														class="ml-3 block text-sm font-medium text-gray-700">Used
														equipment</label>
												</div>
												<div class="flex items-center">
													<input id="new-equipment" name="trade-type" type="radio" value="new"
														class="h-4 w-4 border-gray-300 text-red-600 focus:ring-red-500"
														v-model="selectedTradeType" />
													<label for="new-equipment"
														class="ml-3 block text-sm font-medium text-gray-700">New
														equipment</label>
												</div>
											</div>
										</div>
									</div>
								</div>
								<div class='w-full clearfix mt-4 mb-8'>
									<button v-if='isEditMode' @click='updateCompanyDetails' type="button"
										class="btn btn-primary float-right mr-6">Update Company</button>
								</div>
							</div>
						</div>



						<div class="col-span-12 lg:col-span-6 bg-white shadow overflow-hidden sm:rounded-lg">
							<div class="px-4 py-5 border-b border-gray-200 sm:px-6">
								<h3 class="text-lg leading-6 font-medium text-gray-900">Contact Details</h3>
							</div>

							<div v-if="isFetchingCompanyInfo" class="px-5">
								<div class="grid grid-cols-2 gap-6 py-5 px-1">
									<div class="flex items-start space-x-4 animate-pulse">
										<div class="w-20 h-20 bg-gray-300 rounded-full block"></div>
										<div class="flex-1 space-y-2">
											<div class="h-4 bg-gray-300 rounded w-full"></div>
											<div class="h-4 bg-gray-300 rounded w-4/5"></div>
											<div class="h-4 bg-gray-300 rounded w-3/4"></div>
										</div>
									</div>
									<div class="flex items-start space-x-4 animate-pulse">
										<div class="w-20 h-20 bg-gray-300 rounded-full block"></div>
										<div class="flex-1 space-y-2">
											<div class="h-4 bg-gray-300 rounded w-full"></div>
											<div class="h-4 bg-gray-300 rounded w-4/5"></div>
											<div class="h-4 bg-gray-300 rounded w-3/4"></div>
										</div>
									</div>

									<div class="flex items-start space-x-4 animate-pulse">
										<div class="w-20 h-20 bg-gray-300 rounded-full block"></div>
										<div class="flex-1 space-y-2">
											<div class="h-4 bg-gray-300 rounded w-full"></div>
											<div class="h-4 bg-gray-300 rounded w-4/5"></div>
											<div class="h-4 bg-gray-300 rounded w-3/4"></div>
										</div>
									</div>
								</div>
							</div>
							<div v-else>
								<div class="grid grid-cols-12 gap-6 px-4 sm:px-6 mb-8 max-h-160 overflow-y-auto">
									<div v-for="(contact, index) in contactsEnriched" :key="index"
										class='col-span-12 sm:col-span-6 py-2 sm:gap-4 '>
										<div class='clearfix'>
											<div v-if='contact.profilePic'>
												<div v-if='contact.hasLargePic' @click='showLargePic(contact.id)'
													class="w-12 h-12 rounded-full float-left mr-2 cursor-pointer ring-4"
													alt="profile picture" :style="contact.picCSS">
												</div>
												<div v-else class="w-12 h-12 rounded-full float-left mr-2"
													alt="profile picturexx" :style="contact.picCSS">
												</div>
											</div>
											<div v-else
												class="w-12 h-12 rounded-full float-left mr-2 cursor-pointer ring-4">
												<div v-if='isEditMode && user.id === contact.id'
													@click="showProfilePicModal = true">
													<svg class="h-12 w-12" stroke="none" fill="none"
														viewBox="2 2 16 16">
														<path fill-rule="evenodd" clip-rule="evenodd"
															d="M18 10C18 14.4183 14.4183 18 10 18C5.58172 18 2 14.4183 2 10C2 5.58172 5.58172 2 10 2C14.4183 2 18 5.58172 18 10ZM12 7C12 8.10457 11.1046 9 10 9C8.89543 9 8 8.10457 8 7C8 5.89543 8.89543 5 10 5C11.1046 5 12 5.89543 12 7ZM9.99993 11C7.98239 11 6.24394 12.195 5.45374 13.9157C6.55403 15.192 8.18265 16 9.99998 16C11.8173 16 13.4459 15.1921 14.5462 13.9158C13.756 12.195 12.0175 11 9.99993 11Z"
															fill="gray" />
													</svg>
												</div>
												<div v-else>
													<svg class="h-12 w-12" stroke="none" fill="none"
														viewBox="2 2 16 16">
														<path fill-rule="evenodd" clip-rule="evenodd"
															d="M18 10C18 14.4183 14.4183 18 10 18C5.58172 18 2 14.4183 2 10C2 5.58172 5.58172 2 10 2C14.4183 2 18 5.58172 18 10ZM12 7C12 8.10457 11.1046 9 10 9C8.89543 9 8 8.10457 8 7C8 5.89543 8.89543 5 10 5C11.1046 5 12 5.89543 12 7ZM9.99993 11C7.98239 11 6.24394 12.195 5.45374 13.9157C6.55403 15.192 8.18265 16 9.99998 16C11.8173 16 13.4459 15.1921 14.5462 13.9158C13.756 12.195 12.0175 11 9.99993 11Z"
															fill="gray" />
													</svg>
												</div>
											</div>

											<div class="overflow-hidden break-all break-words">
												<h4 class="mt-2 text-lg leading-6 font-medium text-gray-900 mb-0.5">{{
													contact.name }}</h4>
												<div v-if='contact.label === "Main contact"'
													class="float-left tag  bg-red-100 text-red-800">
													{{ contact.label }}</div>
												<div v-else class="float-left tag bg-gray-100 text-gray-800">{{
													contact.label }}</div>
												<template v-if="user">

													<a v-if="isEditMode && contact.name == 'TBD' && contact.email == '-'"
														class="text-red-400 cursor-pointer ml-2 inline-block"
														@click="editContact(contact)">
														<svg width="20px" height="20px" viewBox="0 0 24 24">
															<title />
															<g id="Complete">
																<g id="edit">
																	<g>
																		<path
																			d="M20,16v4a2,2,0,0,1-2,2H4a2,2,0,0,1-2-2V6A2,2,0,0,1,4,4H8"
																			fill="none" stroke="currentColor"
																			stroke-linecap="round"
																			stroke-linejoin="round" stroke-width="2" />
																		<polygon fill="none"
																			points="12.5 15.8 22 6.2 17.8 2 8.3 11.5 8 16 12.5 15.8"
																			stroke="currentColor" stroke-linecap="round"
																			stroke-linejoin="round" stroke-width="2" />
																	</g>
																</g>
															</g>
														</svg>
													</a>

													<a v-else-if='!isEditMode || user.id !== contact.id'
														href='javascript:void(0)'
														@click='$refs.contactModal.openSendMessageModal(contact)'
														class='inline-block ml-2'>
														<svg class="h-6 w-6 text-gray-400 float-left mr-2" fill="none"
															viewBox="0 0 24 24" stroke="currentColor">
															<path stroke-linecap="round" stroke-linejoin="round"
																stroke-width="2"
																d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
														</svg>
													</a>
												</template>
											</div>
										</div>


										<div v-if='!isEditMode || user.id !== contact.id'>

											<div v-if="contact.function"
												class="text-sm font-semibold text-gray-500 overflow-hidden break-words">
												{{ contact.function }} 
											</div>
											<div v-if="contact.checkinTime"
												class="mr-4 md:mr-0 mt-0 md:mt-2 px-3 py-1 inline-flex text-xs items-center font-bold leading-sm uppercase bg-green-600 text-white rounded-full text-xs">
												Checked In
											</div>
											<div v-else
												class="mr-4 md:mr-0 mt-0 md:mt-2 px-3 py-1 inline-flex text-xs items-center font-bold leading-sm uppercase bg-red-500 text-white rounded-full">
												Not checked in
											</div>

											<div class="my-2"
												v-if="contact.name != 'To be decided' && contact.id != user.id">
												<button class="text-sm flex items-center gap-3 cursor-pointer"
													@click="goToChat(contact.id)">
													<svg width="22px" height="22px" viewBox="0 0 24 24" fill="none">
														<path
															d="M13.0867 21.3877L13.7321 21.7697L13.0867 21.3877ZM13.6288 20.4718L12.9833 20.0898L13.6288 20.4718ZM10.3712 20.4718L9.72579 20.8539H9.72579L10.3712 20.4718ZM10.9133 21.3877L11.5587 21.0057L10.9133 21.3877ZM1.25 10.5C1.25 10.9142 1.58579 11.25 2 11.25C2.41421 11.25 2.75 10.9142 2.75 10.5H1.25ZM3.07351 15.6264C2.915 15.2437 2.47627 15.062 2.09359 15.2205C1.71091 15.379 1.52918 15.8177 1.68769 16.2004L3.07351 15.6264ZM7.78958 18.9915L7.77666 19.7413L7.78958 18.9915ZM5.08658 18.6194L4.79957 19.3123H4.79957L5.08658 18.6194ZM21.6194 15.9134L22.3123 16.2004V16.2004L21.6194 15.9134ZM16.2104 18.9915L16.1975 18.2416L16.2104 18.9915ZM18.9134 18.6194L19.2004 19.3123H19.2004L18.9134 18.6194ZM19.6125 2.7368L19.2206 3.37628L19.6125 2.7368ZM21.2632 4.38751L21.9027 3.99563V3.99563L21.2632 4.38751ZM4.38751 2.7368L3.99563 2.09732V2.09732L4.38751 2.7368ZM2.7368 4.38751L2.09732 3.99563H2.09732L2.7368 4.38751ZM9.40279 19.2098L9.77986 18.5615L9.77986 18.5615L9.40279 19.2098ZM13.7321 21.7697L14.2742 20.8539L12.9833 20.0898L12.4412 21.0057L13.7321 21.7697ZM9.72579 20.8539L10.2679 21.7697L11.5587 21.0057L11.0166 20.0898L9.72579 20.8539ZM12.4412 21.0057C12.2485 21.3313 11.7515 21.3313 11.5587 21.0057L10.2679 21.7697C11.0415 23.0767 12.9585 23.0767 13.7321 21.7697L12.4412 21.0057ZM10.5 2.75H13.5V1.25H10.5V2.75ZM21.25 10.5V11.5H22.75V10.5H21.25ZM7.8025 18.2416C6.54706 18.2199 5.88923 18.1401 5.37359 17.9265L4.79957 19.3123C5.60454 19.6457 6.52138 19.7197 7.77666 19.7413L7.8025 18.2416ZM1.68769 16.2004C2.27128 17.6093 3.39066 18.7287 4.79957 19.3123L5.3736 17.9265C4.33223 17.4951 3.50486 16.6678 3.07351 15.6264L1.68769 16.2004ZM21.25 11.5C21.25 12.6751 21.2496 13.5189 21.2042 14.1847C21.1592 14.8438 21.0726 15.2736 20.9265 15.6264L22.3123 16.2004C22.5468 15.6344 22.6505 15.0223 22.7007 14.2868C22.7504 13.5581 22.75 12.6546 22.75 11.5H21.25ZM16.2233 19.7413C17.4786 19.7197 18.3955 19.6457 19.2004 19.3123L18.6264 17.9265C18.1108 18.1401 17.4529 18.2199 16.1975 18.2416L16.2233 19.7413ZM20.9265 15.6264C20.4951 16.6678 19.6678 17.4951 18.6264 17.9265L19.2004 19.3123C20.6093 18.7287 21.7287 17.6093 22.3123 16.2004L20.9265 15.6264ZM13.5 2.75C15.1512 2.75 16.337 2.75079 17.2619 2.83873C18.1757 2.92561 18.7571 3.09223 19.2206 3.37628L20.0044 2.09732C19.2655 1.64457 18.4274 1.44279 17.4039 1.34547C16.3915 1.24921 15.1222 1.25 13.5 1.25V2.75ZM22.75 10.5C22.75 8.87781 22.7508 7.6085 22.6545 6.59611C22.5572 5.57256 22.3554 4.73445 21.9027 3.99563L20.6237 4.77938C20.9078 5.24291 21.0744 5.82434 21.1613 6.73809C21.2492 7.663 21.25 8.84876 21.25 10.5H22.75ZM19.2206 3.37628C19.7925 3.72672 20.2733 4.20752 20.6237 4.77938L21.9027 3.99563C21.4286 3.22194 20.7781 2.57144 20.0044 2.09732L19.2206 3.37628ZM10.5 1.25C8.87781 1.25 7.6085 1.24921 6.59611 1.34547C5.57256 1.44279 4.73445 1.64457 3.99563 2.09732L4.77938 3.37628C5.24291 3.09223 5.82434 2.92561 6.73809 2.83873C7.663 2.75079 8.84876 2.75 10.5 2.75V1.25ZM2.75 10.5C2.75 8.84876 2.75079 7.663 2.83873 6.73809C2.92561 5.82434 3.09223 5.24291 3.37628 4.77938L2.09732 3.99563C1.64457 4.73445 1.44279 5.57256 1.34547 6.59611C1.24921 7.6085 1.25 8.87781 1.25 10.5H2.75ZM3.99563 2.09732C3.22194 2.57144 2.57144 3.22194 2.09732 3.99563L3.37628 4.77938C3.72672 4.20752 4.20752 3.72672 4.77938 3.37628L3.99563 2.09732ZM11.0166 20.0898C10.8136 19.7468 10.6354 19.4441 10.4621 19.2063C10.2795 18.9559 10.0702 18.7304 9.77986 18.5615L9.02572 19.8582C9.07313 19.8857 9.13772 19.936 9.24985 20.0898C9.37122 20.2564 9.50835 20.4865 9.72579 20.8539L11.0166 20.0898ZM7.77666 19.7413C8.21575 19.7489 8.49387 19.7545 8.70588 19.7779C8.90399 19.7999 8.98078 19.832 9.02572 19.8582L9.77986 18.5615C9.4871 18.3912 9.18246 18.3215 8.87097 18.287C8.57339 18.2541 8.21375 18.2487 7.8025 18.2416L7.77666 19.7413ZM14.2742 20.8539C14.4916 20.4865 14.6287 20.2564 14.7501 20.0898C14.8622 19.936 14.9268 19.8857 14.9742 19.8582L14.2201 18.5615C13.9298 18.7304 13.7204 18.9559 13.5379 19.2063C13.3646 19.4441 13.1864 19.7468 12.9833 20.0898L14.2742 20.8539ZM16.1975 18.2416C15.7862 18.2487 15.4266 18.2541 15.129 18.287C14.8175 18.3215 14.5129 18.3912 14.2201 18.5615L14.9742 19.8582C15.0192 19.832 15.096 19.7999 15.2941 19.7779C15.5061 19.7545 15.7842 19.7489 16.2233 19.7413L16.1975 18.2416Z"
															fill="#1C274C" />
														<path d="M8 11H8.009M11.991 11H12M15.991 11H16" stroke="#1C274C"
															stroke-width="2" stroke-linecap="round"
															stroke-linejoin="round" />
													</svg>Chat
												</button>
											</div>
											<dl class="mt-2 text-sm leading-5 text-gray-500">
												<div class="mt-1" v-if='user && contact.tel'>
													<dt class="sr-only">Telephone</dt>
													<dl>
														<div class="float-left mr-2 mb-3">
															<a
																:href='"whatsapp://send?phone=" + formatWhatsAppNumber(contact.tel)'>
																<svg version="1.1" id="Capa_1"
																	xmlns="http://www.w3.org/2000/svg"
																	xmlns:xlink="http://www.w3.org/1999/xlink"
																	viewBox="0 0 455.731 455.731" width="24" height="24"
																	xml:space="preserve">
																	<g>
																		<rect x="0" y="0" style="fill:#1BD741;"
																			width="455.731" height="455.731" />
																		<g>
																			<path style="fill:#FFFFFF;" d="M68.494,387.41l22.323-79.284c-14.355-24.387-21.913-52.134-21.913-80.638
																	c0-87.765,71.402-159.167,159.167-159.167s159.166,71.402,159.166,159.167c0,87.765-71.401,159.167-159.166,159.167
																	c-27.347,0-54.125-7-77.814-20.292L68.494,387.41z M154.437,337.406l4.872,2.975c20.654,12.609,44.432,19.274,68.762,19.274
																	c72.877,0,132.166-59.29,132.166-132.167S300.948,95.321,228.071,95.321S95.904,154.611,95.904,227.488
																	c0,25.393,7.217,50.052,20.869,71.311l3.281,5.109l-12.855,45.658L154.437,337.406z" />
																			<path style="fill:#FFFFFF;"
																				d="M183.359,153.407l-10.328-0.563c-3.244-0.177-6.426,0.907-8.878,3.037
																	c-5.007,4.348-13.013,12.754-15.472,23.708c-3.667,16.333,2,36.333,16.667,56.333c14.667,20,42,52,90.333,65.667
																	c15.575,4.404,27.827,1.435,37.28-4.612c7.487-4.789,12.648-12.476,14.508-21.166l1.649-7.702c0.524-2.448-0.719-4.932-2.993-5.98
																	l-34.905-16.089c-2.266-1.044-4.953-0.384-6.477,1.591l-13.703,17.764c-1.035,1.342-2.807,1.874-4.407,1.312
																	c-9.384-3.298-40.818-16.463-58.066-49.687c-0.748-1.441-0.562-3.19,0.499-4.419l13.096-15.15
																	c1.338-1.547,1.676-3.722,0.872-5.602l-15.046-35.201C187.187,154.774,185.392,153.518,183.359,153.407z" />
																		</g>
																	</g>
																</svg>
															</a>
														</div>
														<div class="mb-3">
															<a :href='contact.telLink' class='text-red-700'>{{
																contact.tel }}</a>
														</div>
													</dl>
												</div>
												<div class="mt-1" v-if='user && contact.email && isEditMode'>
													<dt class="sr-only">Primary Email</dt>
													<dl>{{ contact.email }}</dl>
												</div>
											</dl>
											<div v-if="!isLoading.availCheck">
												<div v-if="timeslotRequest?.id">
													<div v-if="!isOwnCompany() && enableSchedulerButtons">
														<button v-if="timeslotAvailable(contact.id)" type="button"
															class="button ml-0 w-full"
															@click="requestMeeting(contact, timeslotRequest)">Request
															Meeting on {{ timeslotRequest.day === 1 ? 'Thursday' :
																'Friday' }} from {{ timeslotRequest.timeStart }} -
															{{ timeslotRequest.timeEnd }}</button>
														<button v-else type="button"
															class="button ml-0 w-full bg-red-500 hover:bg-red-400"
															@click="requestMeeting(contact, {})">Not available in this
															timeslot<br />Click for more options</button>
													</div>
												</div>
												<div v-else>
													<button v-if="!isOwnCompany() && enableSchedulerButtons"
														type="button" class="button ml-0 w-full"
														@click="requestMeeting(contact, timeslotRequest)">Request
														Meeting</button>
												</div>
											</div>
										</div>
										<div v-else>
											<div class='mt-6 grid grid-cols-1 row-gap-6 col-gap-4 sm:grid-cols-6'>
												<div class='sm:col-span-6'>
													<label
														class="block text-sm font-medium leading-5 text-gray-700">Contact
														Name</label>
													<div class="mt-1 rounded-md shadow-sm">
														<input v-model="contact.name"
															class="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5">
													</div>
												</div>
											</div>

											<div class='mt-6 grid grid-cols-1 row-gap-6 col-gap-4 sm:grid-cols-6'>
												<div class='sm:col-span-6'>
													<label
														class="block text-sm font-medium leading-5 text-gray-700">Contact
														Telephone</label>
													<div class="mt-1 rounded-md shadow-sm">
														<input v-model="contact.tel"
															class="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5">
													</div>
												</div>
											</div>

											<div class='mt-6 grid grid-cols-1 row-gap-6 col-gap-4 sm:grid-cols-6'>
												<div class='sm:col-span-6'>
													<label
														class="block text-sm font-medium leading-5 text-gray-700">Contact
														Function</label>
													<div class="mt-1 rounded-md shadow-sm">
														<input v-model="contact.function"
															class="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5">
													</div>
												</div>
											</div>


											<div class='mt-6 grid grid-cols-1 row-gap-6 col-gap-4 sm:grid-cols-6'
												v-if='isEditMode'>
												<div class='sm:col-span-6'>
													<label
														class="block text-sm font-medium leading-5 text-gray-700">Primary
														email</label>
													<div class="mt-1" v-if='user && contact.email'>
														<dl>{{ contact.email }}</dl>
													</div>
												</div>
											</div>

											<div class="mt-6" v-if="isEditMode && user && user.id === contact.id && canManagePushNotifications">
												<div class="flex items-start justify-between gap-4">
													<div>
														<div class="text-sm font-medium leading-5 text-gray-700">App notifications</div>
														<p class="mt-1 text-xs text-gray-500">Messages, meeting requests, confirmations, and a reminder 10 minutes before each meeting.</p>
													</div>
													<button
														type="button"
														class="relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none"
														:class="pushEnabled ? 'bg-red-600' : 'bg-gray-300'"
														:aria-pressed="pushEnabled ? 'true' : 'false'"
														aria-label="Toggle app notifications"
														@click="togglePushNotifications"
													>
														<span
															class="pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out"
															:class="pushEnabled ? 'translate-x-5' : 'translate-x-0'"
														/>
													</button>
												</div>
											</div>

											<!-- edit knoppen -->
											<div class='w-full clearfix -mt-4 relative mb-10' v-if='isEditMode'>
												<div class='float-right mt-8'>
													<button @click='updateContactDetails' type="button"
														class="btn btn-primary">Update</button>
													<button type="button" class="ml-3 btn btn-muted"
														@click="showContactEditDropdown = !showContactEditDropdown">
														More
														<svg class="-mr-1 ml-2 h-5 w-5" fill="currentColor"
															viewBox="0 0 20 20">
															<path fill-rule="evenodd"
																d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
																clip-rule="evenodd" />
														</svg>
													</button>
												</div>
												<div v-if='showContactEditDropdown'
													v-on-clickaway="closeContactEditDropdown"
													class="origin-top-right absolute mt-20 right-0 w-56 rounded-md shadow-lg border">
													<div class="rounded-md bg-white shadow-xs">
														<div class="py-1" role="menu" aria-orientation="vertical"
															aria-labelledby="options-menu">
															<a href="javascript:void(0)"
																@click="doShowChangePasswordModal"
																class="block px-4 py-2 text-sm leading-5 text-gray-700 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:bg-gray-100 focus:text-gray-900"
																role="menuitem">Change Password</a>
															<a href="javascript:void(0)"
																@click="showProfilePicModal = true"
																class="block px-4 py-2 text-sm leading-5 text-gray-700 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:bg-gray-100 focus:text-gray-900"
																role="menuitem">Update Profile Picture</a>
															<a v-if='profilePic' href="javascript:void(0)"
																@click="showConfirmDeletePicture = true"
																class="block px-4 py-2 text-sm leading-5 text-gray-700 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:bg-gray-100 focus:text-gray-900"
																role="menuitem">Remove Profile Picture</a>
														</div>
													</div>
												</div>
											</div>

										</div>
									</div>
								</div>
							</div>
						</div>
					</div>

					<modal v-show="showChangePasswordModal" @submit="submitChangePassword"
						@cancel="showChangePasswordModal = false" header="Reset Password"
						affirmativeButtonLabel='Reset Password' :disableSubmit='passwordSubmitDisabled'>
						<template v-slot:body>
							<div>
								<dl class='mt-4 mb-4 sm:-ml-6 sm:-mr-6'>
									<div class=" py-2 sm:grid sm:grid-cols-4 sm:gap-4 sm:px-6">
										<dt class="text-sm leading-9 font-medium text-gray-500">New Password</dt>
										<dd class="mt-1 text-sm leading-5 text-gray-900 sm:mt-0 sm:col-span-3">
											<password v-model="resetPasswordInput" @feedback="passwordFeedBack"
												defaultClass='form-input block w-full' :toggle='true'
												@score='passwordScoreFeedback' />
											<div class='text-blue-600' v-if='passwordSuggestion'>{{ passwordSuggestion
											}}</div>
										</dd>
									</div>
								</dl>
							</div>
						</template>
					</modal>

					<modal-contact ref='contactModal' action='sendMailToContact'></modal-contact>

					<modal v-show="showProfilePicModal" @submit="uploadPictureAndCloseModal"
						@cancel="showProfilePicModal = false" header="Change your profile picture"
						:disableSubmit="!hasImage" affirmativeButtonLabel='Upload picture'>
						<template v-slot:body>
							<div class='mt-8'>
								<image-uploader :debug="2" :maxWidth="400" :maxHeight="400" :quality="0.9" :preview=true
									:className="['fileinput', { 'fileinput--loaded': hasImage }]"
									accept=".jpg, .jpeg, .png" @input="setImage"></image-uploader>
							</div>
						</template>
					</modal>

					<modal v-show="showLargePicModal" @submit="showLargePicModal = false"
						@cancel="showLargePicModal = false" :doSkipHeader="true" :doSkipCancel="true"
						affirmativeButtonLabel='Close'>
						<template v-slot:body>
							<div class='mt-8'>
								<img v-if='profilePicLarge' :src='profilePicLarge' />
								<div v-else>No picture available</div>
							</div>
						</template>
					</modal>

					<modal v-show="showConfirmDeletePicture" @submit="removePictureAndCloseModal()"
						@cancel="showConfirmDeletePicture = false" header="Confirm delete"
						affirmativeButtonLabel='Yes, Delete'>
						<template v-slot:body>
							<div>Are you sure you want to delete this picture?</div>
						</template>
					</modal>

					<modal v-show="showMeetingRequestMessage" @submit="submitMeetingRequest()"
						@cancel="showMeetingRequestMessage = false"
						:header="`Requesting meeting with ${requestContactName}`"
						affirmativeButtonLabel='Request Meeting'>
						<template v-slot:body>
							<div class="mt-4">Anything you would like to add before sending this request?<br><br>
								<textarea v-model="meetingRequestMessage"
									class="form-input block w-full h-24 md:h-32"></textarea>
							</div>
						</template>
					</modal>

					<modal v-show="showMeetingTimeslots" @submit="submitMeetingRequest()"
						@cancel="showMeetingTimeslots = false" :header="`Requesting meeting with ${requestContactName}`"
						:hideSubmit="true">
						<template v-slot:body>
							<div class="mt-4">These are the time slots in which both you and {{ requestContactName }}
								are
								still available. Please select a time slot.</div>
							<nav class="mb-2 flex space-x-8" aria-label="Tabs">
								<button :class="tabClass('0')" @click="currentTab = '0'">Thursday Oct 22</button>
								<button :class="tabClass('1')" @click="currentTab = '1'">Friday Oct 23</button>
							</nav>
							<div v-if="!isLoading.timeslots">
								<div v-for="timeslot in currentTimeslots" :key="timeslot.id"
									class="relative items-center space-x-3 rounded-lg shadow-sm focus-within:ring-2 focus-within:ring-red-500 focus-within:ring-offset-2 border"
									:class="{ 'timeslot-used': isTimeslotUsed(timeslot.id) }">
									<div>
										<div v-if="isTimeslotUsed(timeslot.id)"
											class="schedule-header text-white px-4 py-2 rounded-lg"
											:class="{ 'bg-gray-300': isTimeslotUsed(timeslot.id), 'bg-blue-500 hover:bg-blue-600': !isTimeslotUsed(timeslot.id) }">
											{{ timeslot.timeStart }} - {{ timeslot.timeEnd }}
											<div v-if="isTimeslotUsed(timeslot.id)" class="float-right"><em>Not
													available</em></div>
										</div>
										<div v-else-if="timeslot.isFullyBooked"
											class="schedule-header text-white px-4 py-2 rounded-lg bg-gray-300">
											{{ timeslot.timeStart }} - {{ timeslot.timeEnd }}
											<div class="float-right"><em>Fully Booked</em></div>
										</div>
										<div v-else
											class="schedule-header text-white px-4 py-2 rounded-lg cursor-pointer"
											:class="{ 'bg-gray-300': isTimeslotUsed(timeslot.id), 'bg-blue-500 hover:bg-blue-600': !isTimeslotUsed(timeslot.id) }"
											@click="requestMatchedMeeting(timeslot)">
											{{ timeslot.timeStart }} - {{ timeslot.timeEnd }}
											<div class="float-right">Request meeting </div>
										</div>
									</div>
								</div>
							</div>
						</template>
					</modal>


					<modal v-show="showEditContactModal" @submit="saveContact" @cancel="showEditContactModal = false"
						header="Edit Contact Information" affirmativeButtonLabel="Save">
						<template v-slot:body>

							<ValidationObserver ref="editContactObserver">
								<div v-if="contactToEdit" class="space-y-4 mt-4">

									<!-- Name -->
									<div class="grid grid-cols-1 sm:grid-cols-4 sm:gap-4 items-start">
										<label class="text-sm font-medium text-gray-700 sm:pt-2">
											Name
										</label>

										<div class="sm:col-span-3">
											<ValidationProvider name="Name" rules="required" v-slot="{ errors }">
												<input v-model="contactToEdit.name" ref="editContactName"
													class="form-input block w-full">
												<span class="text-red-500 text-sm">{{ errors[0] }}</span>
											</ValidationProvider>
										</div>
									</div>

									<!-- Email -->
									<div class="grid grid-cols-1 sm:grid-cols-4 sm:gap-4 items-start">
										<label class="text-sm font-medium text-gray-700 sm:pt-2">
											Email
										</label>

										<div class="sm:col-span-3">
											<ValidationProvider name="Email" rules="required|email" v-slot="{ errors }">
												<input v-model="contactToEdit.email" class="form-input block w-full">
												<span class="text-red-500 text-sm">{{ errors[0] }}</span>
											</ValidationProvider>
										</div>
									</div>

									<!-- Function -->
									<div class="grid grid-cols-1 sm:grid-cols-4 sm:gap-4 items-start">
										<label class="text-sm font-medium text-gray-700 sm:pt-2">
											Function
										</label>

										<div class="sm:col-span-3">
											<ValidationProvider name="Function" rules="required" v-slot="{ errors }">
												<input v-model="contactToEdit.function" class="form-input block w-full">
												<span class="text-red-500 text-sm">{{ errors[0] }}</span>
											</ValidationProvider>
										</div>
									</div>

									<!-- Telephone -->
									<div class="grid grid-cols-1 sm:grid-cols-4 sm:gap-4 items-start">
										<label class="text-sm font-medium text-gray-700 sm:pt-2">
											Telephone
										</label>

										<div class="sm:col-span-3">
											<ValidationProvider name="Telephone" rules="required" v-slot="{ errors }">
												<input v-model="contactToEdit.tel" class="form-input block w-full">
												<span class="text-red-500 text-sm">{{ errors[0] }}</span>
											</ValidationProvider>
										</div>
									</div>
								</div>
							</ValidationObserver>

						</template>
					</modal>

				</div>

			</div>
		</template>
	</base-layout>
</template>

<script>

import { mapState, mapActions } from 'vuex'
import _ from 'lodash';
import { mixin as clickaway } from 'vue-clickaway';
import ImageUploader from "vue-image-upload-resize"

import BaseLayout from '@/components/BaseLayout.vue'
import Modal from "@/components/modal"
import ModalContact from '@/components/ModalContact.vue'
import resetPassword from '@/mixins/resetPassword'

import store from '../store'
import eventBus from '../utils/eventBus'
import { setPushPreference, wantsPushNotifications } from '../utils/pushNotifications'
import { isPwaInstallAllowed } from '../utils/pwaInstall'
import Contact from './Contact.vue';

export default {
	name: 'CompanyDetail',
	components: {
		BaseLayout,
		Modal,
		ModalContact,
		ImageUploader,
	},
	mixins: [clickaway, resetPassword],
	// beforeRouteEnter(to, from, next) {
	// 	let isProfile = to.name === 'profile'
	// 	let companyId = to.params.id
	// 	next()
	// 	if (isProfile) {
	// 		store.commit("bootstrapUserFromLocalStorage")
	// 		if (!store.state.user) {
	// 			next({ name: 'home' })
	// 			return
	// 		}
	// 		companyId = store.state.user.companyId
	// 	}
	// 	store.dispatch('getCompany', companyId).then(resp => {
	// 		if (resp.status !== 200) throw new Error("TODO: not found. show error page")
	// 		next(vm => {
	// 			vm.selectedCompany = resp.company;
	// 			vm.contacts = resp.contacts;
	// 			vm.isEditMode = isProfile;
	// 			document.title = isProfile ? `Profile - European Broker Meeting` : `${vm.selectedCompany.name} - European Broker Meeting`
	// 		})
	// 	})
	// },
	data() {
		return {
			contacts: [],
			selectedCompany: {},
			showBankInfo: false,
			isRecommendModalVisible: false,
			isRecommendationInvalid: true,
			recommendationText: "",

			showProfilePicModal: false,
			showConfirmDeletePicture: false,
			showMeetingRequestMessage: false,
			showMeetingTimeslots: false,
			requestContactName: null,
			picBase64: null,
			hasImage: false, //selected in image upload widget?

			showLargePicModal: false,
			profilePicLarge: null,

			isEditMode: false,
			enableSchedulerButtons: false, // in case we want to prevent scheduler actions from the attendee/company details page
			meetingRequest: [],
			meetingRequestMessage: null,
			takenTimeslotsAllContacts: {},
			takenTimeslotsMatched: {},
			timeslotsByDay: null,
			isLoading: {
				availCheck: false,
				timeslots: true,
			},
			days: null,
			numTables: null,
			numTablesMax: null,
			currentTab: "0", // 0 (day 1), 1 (day 2)
			usedTimeslotIds: null,
			selectedContact: null,
			maxCharacters: 350,
			payload: null,
			selectedCategories: [], // This will hold the selected category IDs
			selectedTradeType: "",  // This will hold the selected trade type
			errors: [],             // This will hold validation error messages
			companyLogoURL: null,
			isLogoLoaded: false,
			notAvailableContacts: [],
			isFetchingCompanyInfo: false,
			showDownloadSharingBanner: false,
			showEditContactModal: false,
			contactToEdit: null
		}
	},
	watch: {
		'$route.params.id'(newId) {
			window.location.reload();
		},
		selectedCompany(newVal) {
			if (newVal) {
				this.initializeForm();
				this.checkCompanyLogo();
			}
		},
		selectedCategories(newVal) {
			this.errors = [];

			// Enforce a minimum of 1 selection
			if (newVal.length === 0) {
				this.errors.push('Please select at least one option.');
			}

			// Enforce a maximum of 3 selections
			if (newVal.length > 3) {
				this.errors.push('You can select a maximum of 3 options.');
			}
		},
	},
	created() {
		this.getCompanyInfo()
		const tsRequest = JSON.parse(localStorage.getItem('tsRequest'))
		const tsRequestSession = JSON.parse(sessionStorage.getItem('tsRequest'))

		if (tsRequest) { // This will only be true if the user is from the attendees.vue
			this.$store.dispatch('updateTimeslotRequest', tsRequest)
			sessionStorage.setItem('tsRequest', JSON.stringify(tsRequest))
			localStorage.removeItem("tsRequest")
		} else if (tsRequestSession) {
			this.$store.dispatch('updateTimeslotRequest', tsRequestSession)
		}
		this.initializeForm();
	},
	async mounted() {
		this.getNotAvailableContacts()

		// if (this.user.companyId==7 || this.user.companyId==244) {this.enableSchedulerButtons = true} // added temporarily to enable scheduler buttons for testing, only for the test user with the IDs 23 and 481
		this.allSettings = await this.$http.get("/api/misc/allSettings").then(res => res.data.payload)
		this.enableSchedulerButtons = Boolean(Number(this.allSettings.schedulerEnabled))

	},
	computed: {
		...mapState(["route", 'user', 'profilePic', 'timeslotRequest', 'categoryOptions']),
		categoryOptionsEnriched() {
			return _.map(this.categoryOptions, option => ({
				...option,
				vueKey: `option-${option.id}`
			}))
		},
		currentTimeslots() {
			return this.timeslotsByDay[+this.currentTab + 1]
		},
		selectedCompanyEnriched() {
			return this.selectedCompany;
		},
		contactsEnriched() {
			return this.contacts;
		},
		vatLabel() {
			return this.selectedCompanyEnriched.country === 'UK' ? "EORI" : "VAT"
		},
		remainingCharacters() {
			return this.maxCharacters - this.selectedCompanyEnriched.profile.length;
		},
		pushEnabled() {
			return wantsPushNotifications(this.user)
		},
		canManagePushNotifications() {
			return isPwaInstallAllowed(this.user)
		},
	},

	methods: {
		...mapActions(["updateCompany", "updateContact", "uploadPicture", 'removePicture', 'checkAvailability', 'getSharingBanner']),
		openBadgeForDownload(badgeType) {
			window.open('/assets/' + badgeType + ".png")
		},

		async checkCompanyLogo() {
			const baseUrl = "https://www.thebrokersite.com/images/logos/";
			const extensions = ["jpg", "jpeg", "png"];
			const username  = this.selectedCompany.customLogo || this.selectedCompany.username;
			

			for (const ext of extensions) {
				const url = `${baseUrl}${username}.${ext}`;
				try {
					const img = new Image();

					await new Promise((resolve, reject) => {
						img.onload = () => {
							this.companyLogoURL = url;
							this.isLogoLoaded = true;
							if (this.user && this.selectedCompany.id == this.user.companyId) {
								// this.showDownloadSharingBanner = true;
							}
							resolve();
						};
						img.onerror = reject;
						img.src = url;
					});

					return;
				} catch (error) {
					console.error("Image not found:", url);
				}
			}
		},
		async getAllTimeSlots() {
			let timeslots
			await this.$http.get("/api/scheduler/timeslots").then(res => {
				timeslots = res.data.timeslots
				this.days = [res.data.metaData.date_start.value, res.data.metaData.date_end.value] // always 2
				this.numTables = res.data.metaData.meeting_tables.value
				this.numTablesMax = res.data.metaData.meeting_tables_max.value // Including lobby seats
				this.timeslotsByDay = _.groupBy(timeslots, 'day')
			})
		},
		initializeForm() {
			const companyData = this.selectedCompanyEnriched;
			if (companyData && companyData.categories) {
				const categoryNames = companyData.categories.split(', ');
				this.selectedCategories = this.categoryOptions
					.filter(option => categoryNames.includes(option.value))
					.map(option => option.id);

				// Set selected trade type
				this.selectedTradeType = companyData.tradingType === "Used equipment" ? "used" :
					companyData.tradingType === "New equipment" ? "new" : "";
			}
		},
		async updateCompanyDetails() {
			// Check if remaining characters are below 0
			if (this.remainingCharacters < 0) {
				eventBus.$emit("createSnackbar", {
					type: "error",
					title: "Character limit exceeded",
					body: "Please reduce the number of characters in the Company Description field to proceed.",
					timeoutInMS: 5000,
				});
				return; // Exit the method without submitting
			}

			// Check if at least one category is chosen
			if (this.selectedCategories.length < 1) {
				eventBus.$emit("createSnackbar", {
					type: "error",
					title: "No main interest selected",
					body: "Please choose at least one main interest.",
					timeoutInMS: 5000,
				});
				return; // Exit the method without submitting
			}

			// process the preferences and trading types and update them in the selectedCompanyEnriched
			this.selectedCategories.sort((a, b) => a - b);
			const preferences = {
				categories: this.selectedCategories,
				trading: this.selectedTradeType
			};
			this.selectedCompanyEnriched.preferences = JSON.stringify(preferences)

			const selectedCompanyDTO = _.pick(this.selectedCompanyEnriched, ['id', 'tel', 'address', 'profile', 'website', 'preferences'])
			const resp = await this.updateCompany(selectedCompanyDTO);

			if (![200, 501, 502].includes(resp.data.status)) {
				eventBus.$emit("createSnackbar", {
					type: "error",
					title: "Something went wrong",
					body: `Company wasn't updated. Please try again. <br/> If the problem persists please contact European Broker Meeting (Code ${resp.data.status})`,
					timeoutInMS: 5000,
				})
			} else {
				eventBus.$emit("createSnackbar", {
					type: "success",
					title: "Company profile succesfully updated"
				})
			}
		},
		async updateContactDetails() {

			const contactDTO = _.pick(_.find(this.contactsEnriched, { id: this.user.id }), ['id', 'tel', 'function', 'name', 'imName', 'imValue', 'rfqDisabled', 'possibleMatchesDisabled']);

			const resp = await this.updateContact(contactDTO);

			if (resp.data.status !== 200) {
				eventBus.$emit("createSnackbar", {
					type: "error",
					title: "Something went wrong",
					body: "Contact wasn't updated. Please try again. <br/> If the problem persists please contact European Broker Meeting",
					timeoutInMS: 5000,
				})
			} else {
				eventBus.$emit("createSnackbar", {
					type: "success",
					title: "Contact succesfully updated",

				})
			}
		},
		async togglePushNotifications() {
			const nextEnabled = !this.pushEnabled
			try {
				await setPushPreference(nextEnabled)
				eventBus.$emit("createSnackbar", {
					type: "success",
					title: nextEnabled ? "Notifications turned on" : "Notifications turned off",
				})
			} catch (err) {
				eventBus.$emit("createSnackbar", {
					type: "error",
					title: "Could not update notifications",
					body: "Please try again. If the problem persists please contact European Broker Meeting",
					timeoutInMS: 5000,
				})
			}
		},
		async getNotAvailableContacts() {
			try {
				const companyId = this.$route.params.id
				if (this.timeslotRequest.id && companyId) {
					const payload = {
						timeslotId: this.timeslotRequest.id,
						companyId: companyId
					}
					const res = await this.checkAvailability(payload)
					this.notAvailableContacts = res.data.notAvailableContacts
				}
			} catch (error) {
				console.error('Error in processing timeslot data:', error);
			}
		},
		async removePictureAndCloseModal() {
			try {
				const resp = await this.removePicture()
				if (resp.data.status !== 200) throw resp
			} catch (err) {
				eventBus.$emit("createSnackbar", {
					type: "error",
					title: "Picture could not be deleted",
					body: "Please try again. If this occur keeps occuring, please contact European Broker Meeting",
					timeoutInMS: 5000,
				})
			}
			this.showConfirmDeletePicture = false
			this.$router.go(this.$router.currentRoute)
		},
		async uploadPictureAndCloseModal() {
			try {
				const resp = await this.uploadPicture({ picture: this.picBase64 })
				if (resp.data.status !== 200) throw resp

				this.closeUploadPictureModal()
				this.$router.go(this.$router.currentRoute)

			} catch (err) {
				eventBus.$emit("createSnackbar", {
					type: "error",
					title: "Profile Picture could not be uploaded",
					body: "Please try again. If this occur keeps occurring, please contact European Broker Meeting",
					timeoutInMS: 5000,
				})
			}

		},
		closeUploadPictureModal() {
			this.picBase64 = null;
			this.showProfilePicModal = false
		},
		async showLargePic(id) {
			const resp = await this.$http.get(`/api/companies/fetchLargePicture/${id}`)
			this.profilePicLarge = resp.data.profilePicLarge || null
			this.showLargePicModal = true;
		},
		async requestMeeting(targetContact, timeslot) {
			this.requestContactName = targetContact.name
			this.selectedContact = targetContact

			if (timeslot?.id) {
				await this.getNotAvailableContacts()
				if (this.notAvailableContacts.includes(targetContact.id)) {
					return
				}

				this.meetingRequest.toId = targetContact.id
				this.meetingRequest.timeslotId = timeslot.id
				this.showMeetingRequestMessage = true
			} else {
				this.isLoading.timeslots = true
				try {
					const url = `/api/scheduler/contacts/${targetContact.id}/action/findCombinedTakenTimeslots`
					const resp = await this.$http.get(url)
					this.usedTimeslotIds = resp.data.usedTimeslotIds
					await this.getAllTimeSlots()
					this.showMeetingTimeslots = true
					this.isLoading.timeslots = false
				} catch (err) {
					console.error('Error during request cancellation:', err)
				}
			}
		},
		async requestMatchedMeeting(timeslot) {
			this.requestContactName = this.selectedContact.name
			this.meetingRequest.toId = this.selectedContact.id
			this.meetingRequest.timeslotId = timeslot.id
			this.showMeetingTimeslots = false
			this.showMeetingRequestMessage = true
		},
		async submitMeetingRequest() {
			const payload = {
				toId: this.meetingRequest.toId,
				timeslotId: this.meetingRequest.timeslotId,
				message: this.meetingRequestMessage,
			}
			this.showMeetingRequestMessage = false
			const resp = await this.$http.post("/api/scheduler/meetings", payload)
			if (resp.data.status !== 200) {
				eventBus.$emit("createSnackbar", {
					type: "error",
					title: "There was a problem sending your meeting request",
					body: resp.data.payload?.msg || '',
					timeoutInMS: 5000,
				})
			} else {
				eventBus.$emit("createSnackbar", {
					type: "success",
					title: "Meeting request was successfully sent",
				})

				// setTimeout(() => {
				this.$router.push(`/scheduler?cd=${resp.data.currentlySelectedDay}`);
				// }, 1000)
			}
		},
		async getAvailableTimeSlotIds(contactId) {
			try {
				const url = `/api/scheduler/contacts/${contactId}/action/findTakenTimeslots`
				const response = await this.$http.get(url)
				return response.data.usedTimeslotIds // array with timeslots that are taken, use this to check if the current timeslot is in it
			} catch (error) {
				console.error('Error during request cancellation:', error)
			}
		},
		async getCompanyInfo() {
			this.isFetchingCompanyInfo = true
			const isProfile = this.$route.name === 'profile'
			let companyId = this.$route.params.id
			this.isEditMode = isProfile

			if (isProfile) {
				store.commit("bootstrapUserFromLocalStorage")
				if (!store.state.user) {
					this.$router.push({ name: 'home' })
					return
				}
				companyId = store.state.user.companyId
			}
			try {
				const res = await this.$store.dispatch('getCompany', companyId)
				if (res.status !== 200) {
					// throw new Error("TODO: not found. show error page");
					this.$router.push({ name: 'home' })
				}

				this.selectedCompany = res.company,
					// this.contacts = res.contacts
					this.contacts = res.contacts.map(c => ({
						...c,
						profilePic: null
					}));
				document.title = this.isEditMode
					? `Profile - European Broker Meeting`
					: `${this.selectedCompany.name} - European Broker Meeting`
				this.isFetchingCompanyInfo = false
				this.fetchProfilePictures()

			} catch (err) {
				this.$router.push({ name: 'home' })
			}

		},
		async fetchProfilePictures() {
			try {

				let companyId = this.$route.params.id
				if (this.$route.name === 'profile') {
					companyId = store.state.user.companyId
				}
				const res = await this.$http.post(`/api/companies/fetchProfilePictures`, { companyId })


				if (res.status === 200) {
					res.data.pictures.forEach(pic => {

						const contact = this.contacts.find(c => c.id === pic.id)
						if (contact) {
							contact.profilePic = pic.profilePic;
							contact.picCSS = {
								background: `url('${contact.profilePic}') no-repeat right center / cover`,
							}
							if (this.user.id == pic.id && pic.profilePic) {
								// this.showDownloadSharingBanner = true;
							}
						}
					});
				}
			} catch (err) {
				console.error("Failed to load profile pictures", err);
			}
		},


		closeContactEditDropdown() {
			this.showContactEditDropdown = false;
		},
		showBroadcastSettings() {
			this.$refs.BroadcastSettings.$emit("showModal")
			this.showContactEditDropdown = false;
		},
		checkRecommendationValid() {
			this.isRecommendationInvalid = this.recommendationText.length < 30
		},
		showRecommendModal() {
			this.isRecommendModalVisible = true;
			this.isRecommendationInvalid = false; //reset validation
		},
		closeRecommendModal() {
			this.isRecommendModalVisible = false;
		},
		setImage(picBase64) {
			this.hasImage = true
			this.picBase64 = picBase64
		},
		openComplaintModal() {
			this.$parent.$parent.openComplaintModal()
		},
		isOwnCompany() {
			return this.user.companyId === this.selectedCompany.id
		},
		timeslotAvailable(contactId, timeslotId) {
			return !this.notAvailableContacts.includes(contactId)
		},
		isTimeslotUsed(timeslotId) {
			return this.usedTimeslotIds.includes(timeslotId)
		},
		tabClass(index) {
			// console.log(this.currentTab)
			this.currentTab = String(this.currentTab) // Added by Brian: convert to string, because when using mobile, the value is passed as an integer and the === won't work, results in only displaying the cancelled meetings tab
			if (+index === +this.currentTab) return 'border-red-500 text-red-600 flex whitespace-nowrap border-b-2 py-4 px-1 font-medium'
			return 'border-transparent text-gray-500 hover:border-gray-200 hover:text-gray-700 flex whitespace-nowrap border-b-2 py-4 px-1font-medium'
		},
		formatWhatsAppNumber(phoneString) {
			const firstPhoneString = phoneString.split(',')[0].trim();
			const cleanedString = firstPhoneString.replace(/[^\d]/g, '');
			return cleanedString;
		},
		updateRemainingCharacters() {
			this.remainingCharacters = this.maxCharacters - this.selectedCompanyEnriched.profile.length;
		},
		isCheckboxDisabled(id) {
			return this.selectedCategories.length >= 3 && !this.selectedCategories.includes(id);
		},
		goToChat(id) {
			const url = this.$router.resolve({ path: '/messages', query: { contact: id } }).href;
			window.open(url, '_blank');
		},
		normalizeWebsiteUrl(url) {
			if (!url || url === '-') return url;
			// Check if URL already has a protocol
			if (/^https?:\/\//i.test(url)) {
				return url;
			}
			// Prepend https:// if no protocol is present
			return `https://${url}`;
		},
		async downloadSharingBanner() {
			const payload = {
				contactId: this.user.id,
				email: this.user.email,
				companyId: this.user.companyId
			}

			const resp = await this.getSharingBanner(payload);

			if (resp.data.status != 200) {
				eventBus.$emit("createSnackbar", {
					type: "error",
					title: resp.data?.message,
					timeoutInMS: 5000,
				});
				return; // Exit the method without submitting
			} else {
				if (resp.data?.downloadPath) {
					let imageUrl = resp.data.downloadPath;

					// Normalize origin to current site
					imageUrl = imageUrl.replace(
						/^https?:\/\/(www\.)?europeanbrokermeeting\.com/i,
						window.location.origin
					);

					// Fetch the image
					const response = await fetch(imageUrl);

					const blob = await response.blob();

					// Create blob URL
					const blobUrl = window.URL.createObjectURL(blob);

					// Download
					const link = document.createElement("a");
					link.href = blobUrl;
					link.download = imageUrl.split("/").pop() || "sharing-banner.png";

					document.body.appendChild(link);
					link.click();
					document.body.removeChild(link);

					// Cleanup
					window.URL.revokeObjectURL(blobUrl);
				}
			}

		},

		editContact(contact) {
			this.contactToEdit = { ...contact };
			this.showEditContactModal = true;
			this.$nextTick(() => {
				this.$refs.editContactName?.focus();
			});
		},

		async saveContact() {
			const { id, name, email, tel, function: func } = this.contactToEdit;

			const payload = {
				id,
				name,
				email,
				tel,
				function: func
			};

			const res = await this.$http.put("/api/contacts", payload)
			if (res.data.status === 200) {
				this.getCompanyInfo();
				eventBus.$emit("createSnackbar", {
					type: "success",
					title: "Contact information succesfully updated"
				})
				this.showEditContactModal = false;
			} else {
				eventBus.$emit("createSnackbar", {
					type: "error",
					title: "Error updating the contact information",
					body: res.data.payload.msg,
					timeoutInMS: 5000,
				});
			}

		}
	},
}
</script>
<style scoped>
.member_normal {
	width: 40px;
	height: 40px;
	background: url("../assets/normal.png");
	background-size: cover;
}

.member_trusted {
	width: 40px;
	height: 40px;
	background: url("../assets/trusted.png");
	background-size: cover;
}

.member_gold {
	width: 40px;
	height: 40px;
	background: url("../assets/gold.png");
	background-size: cover;
}

.member_diamond {
	width: 40px;
	height: 40px;
	background: url("../assets/diamond.png");
	background-size: cover;
}
</style>
